import { useEffect, useRef } from 'react';
import { pauseAllAudio, resumeAllAudio, stopAllAudio } from './musicState';

/**
 * Hook to handle audio pause/resume on screen lock/unlock
 * Aggressively stops all audio when device screen is locked or page becomes hidden
 * Resumes audio when screen is unlocked or page becomes visible
 * 
 * This matches the behavior of other games where music stops when the screen is locked
 * Enhanced for mobile devices where screen-off events may not fire reliably
 */
export function useScreenLockAudio() {
  const checkIntervalRef = useRef(null);
  const isPausedRef = useRef(false);

  useEffect(() => {
    // Aggressive audio stopping function for mobile
    const stopAllAudioAggressively = () => {
      if (isPausedRef.current) return; // Already stopped
      
      console.log('📱 Screen/page hidden - stopping all audio aggressively');
      isPausedRef.current = true;
      
      // Use the pause function which remembers state for resume
      pauseAllAudio();
      
      // Also aggressively stop ALL audio elements in the DOM
      try {
        const allAudioInDOM = document.querySelectorAll('audio');
        allAudioInDOM.forEach(audio => {
          if (audio) {
            try {
              audio.pause();
              audio.volume = 0;
              audio.currentTime = 0;
            } catch (e) {
              // Ignore errors
            }
          }
        });
      } catch (e) {
        // Ignore errors
      }
      
      // Stop all audio using the global stop function
      stopAllAudio();
    };

    // Handle visibility change (covers screen lock, tab switch, minimize, etc.)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAllAudioAggressively();
        
        // Start periodic check to ensure audio stays stopped on mobile
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }
        checkIntervalRef.current = setInterval(() => {
          if (document.hidden) {
            // Double-check that all audio is stopped
            try {
              const allAudioInDOM = document.querySelectorAll('audio');
              allAudioInDOM.forEach(audio => {
                if (audio && !audio.paused) {
                  console.log('📱 Mobile: Detected audio still playing while screen off - stopping');
                  audio.pause();
                  audio.volume = 0;
                }
              });
            } catch (e) {
              // Ignore errors
            }
          } else {
            // Page is visible again, stop checking
            if (checkIntervalRef.current) {
              clearInterval(checkIntervalRef.current);
              checkIntervalRef.current = null;
            }
          }
        }, 500); // Check every 500ms when screen is off
      } else {
        // Page is visible again (screen unlocked, tab focused, etc.)
        console.log('📱 Screen/page visible - resuming audio');
        isPausedRef.current = false;
        
        // Stop the periodic check
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        
        // Small delay to ensure page is fully active
        setTimeout(() => {
          resumeAllAudio();
        }, 100);
      }
    };

    // Handle page hide/show (additional coverage for mobile)
    const handlePageHide = () => {
      stopAllAudioAggressively();
    };

    const handlePageShow = () => {
      console.log('📱 Page shown - resuming audio');
      isPausedRef.current = false;
      
      // Small delay to ensure page is fully active
      setTimeout(() => {
        resumeAllAudio();
      }, 100);
    };

    // Handle window blur/focus (covers some edge cases)
    const handleBlur = () => {
      // Only pause if page is also hidden (to avoid double-pausing)
      if (document.hidden) {
        stopAllAudioAggressively();
      }
    };

    const handleFocus = () => {
      // Only resume if page is visible (to avoid double-resuming)
      if (!document.hidden) {
        isPausedRef.current = false;
        console.log('📱 Window focused and page visible - resuming audio');
        setTimeout(() => {
          resumeAllAudio();
        }, 100);
      }
    };

    // Mobile-specific: Handle freeze/resume events (when browser is frozen/unfrozen)
    const handleFreeze = () => {
      console.log('📱 Browser frozen (mobile screen off) - stopping all audio');
      stopAllAudioAggressively();
    };

    const handleResume = () => {
      console.log('📱 Browser resumed (mobile screen on) - resuming audio');
      isPausedRef.current = false;
      setTimeout(() => {
        resumeAllAudio();
      }, 100);
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    
    // Mobile-specific events (supported in some browsers)
    document.addEventListener('freeze', handleFreeze);
    document.addEventListener('resume', handleResume);

    // Initial check - if page is already hidden, stop audio immediately
    if (document.hidden) {
      stopAllAudioAggressively();
    }

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('freeze', handleFreeze);
      document.removeEventListener('resume', handleResume);
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, []);
}

